<template>
  <div>
    <header class="credit-app-header">
      <div class="logo">
        <a class="icon-saxx-logo"></a>
      </div>
    </header>

    <main v-if="!isSubmitted" class="credit-app-main">
      <el-dialog title="Error" :visible.sync="errors" width="30%">
        <p>{{ errors }}</p>
      </el-dialog>

      <div class="card card--form">
        <header class="card__header">
          <h2>{{ language.title_credit_application }}</h2>
        </header>

        <ApplicationForm :lang="lang" :formName="formName" @submit="submit" />
      </div>
    </main>
    <main v-else class="credit-app-main">
      <div class="card__header">
        <h2>
          {{ language.text_credit_application_confirmation }}
        </h2>
      </div>
    </main>
  </div>
</template>

<script>
  import { ErrorlessRepository as axios } from "../../repositories/Repository";
  import { mapActions, mapGetters } from "vuex";
  import { notify, notifyClose } from "../../utils";
  import { language } from '../../lang';

  import ApplicationForm from './ApplicationForm';
  import fi from "element-ui/src/locale/lang/fi";

  export default {
    name: "CreateApplication",
    components: {
      ApplicationForm,
    },
    props: {
      token: {
        required: true,
        type: String,
      },
      lang: {
        required: false,
        type: String,
        default: "en",
      },
    },
    data() {
      return {
        formName: "b2b.new-applications",
        errors: false,
      };
    },
    mounted() {
      this.fetchToken(this.token);
    },
    methods: {
      ...mapActions("forms", [
        "setStatus",
        "setFields",
        "setErrors",
        "setDisabledFields",
      ]),
      async fetchToken(token) {
        this.setStatus({
          form: this.formName,
          status: "loading"
        });

        try {
          const response = await axios.get(
            `/credit-dashboard/access-tokens/${token}`
          );
          const data = response.data;

          this.setFields({
            form: this.formName,
            fields: {
              customerEmail: data.customer_email,
              repName: data.rep_name,
              repEmail: data.rep_email,
            }
          });

          if (data.account) {
            const a = data.account;
            this.setFields({
              form: this.formName,
              fields: {
                tradeName: a.accountName,
                country: a.country,
                province: a.province,
                city: a.city,
                address: a.accountAddress,
                address2: a.accountAddress2,
                postal: a.postalCode,
                website: a.website,
                agency: a.agency,
                channel: a.channel,
              }
            });

            this.setDisabledFields({
              form: this.formName,
              fields: {
                agency: true,
                channel: true,
                repName: true,
                repEmail: true,
              },
            })
          }
        } catch (err) {
          if (!err.response) {
            this.errors = err.message;
            return;
          }

          if (err.response.status === 404) {
            this.errors = "You do not have access to fill out this form.";
            return;
          }

          this.errors = err.response.data.errors;
        } finally {
          this.setStatus({
            form: this.formName,
            status: "idle"
          });
        }
      },
      async submit(fields) {
        try {
          this.setStatus({
            form: this.formName,
            status: "loading"
          });

          fields.additional_addresses=JSON.stringify(fields.additional_addresses);
          const response = await axios.post(
            `/credit-dashboard/access-tokens/${this.token}/applications`,
            this.toFormData(fields),
            {
              headers: { "content-type": "multipart/form-data" }
            }
          );

          this.setStatus({
            form: this.formName,
            status: "submitted"
          });
        } catch (err) {
          this.setStatus({
            form: this.formName,
            status: "idle"
          });

          if (!err.response) {
            return;
          }
        if (!err.response.data) {
          return;
        }

        const responseData = err.response.data;
        const errorPayload = responseData.errors;
        if (!errorPayload || typeof errorPayload !== "object") {
          return;
        }

        this.setErrors({
          form: this.formName,
          errors: errorPayload
        });

        notify(
          "There were issues with the following fields in the form: " + Object.keys(errorPayload).join(", ") + ". Please review each issue and retry.",
          "danger"
        );
        }
      },
      toFormData(fields) {
        let form = new FormData();
        Object.keys(fields).forEach(key => {
          const val = fields[key];
          if (val === null) {
            return;
          }

          if (Array.isArray(val)) {
            val.forEach((v, index) => form.append(`${key}[${index}]`, v));
            return;
          }

          if (typeof val === "boolean") {
            return form.append(key, Number(val));
          }

          if (typeof val === "string" && val.trim() === "") {
            return;
          }

          form.append(key, val);
        });

        return form;
      },
    },
    computed: {
      ...mapGetters("forms", ["getStatus"]),
      language() {
        const lang = language(this.lang);

        return lang.creditDashboard;
      },
      isSubmitted() {
        return this.getStatus({ form: this.formName }) === "submitted";
      },
    },
    watch: {
      isSubmitted(val) {
        if (!val) {
          return;
        }

        notifyClose();
      }
    }
  };
</script>
